import usePrivateAxios from '@/common/api/usePrivateAxios'
import { ErrorResponse } from '@/common/types/errorResponse'
import { getRailsUpdate } from '@/common/utils/getRailsUpdate'
import { mapToDeletion, reconcileRailsUpdate } from '@/common/utils/railsHelpers'
import { MaintenanceSolicitationValidationSchema } from '@/features/maintenanceSolicitations/schemas/create.schema'
import { MaintenanceSolicitationData } from '@/features/maintenanceSolicitations/types/maintenanceSolicitation'
import { useMutation } from '@tanstack/react-query'

interface UseUpdateMaintenanceSolicitationProps {
  id: string
  maintenanceSolicitation?: MaintenanceSolicitationData
  onSuccess: () => void
  onError: (error: ErrorResponse) => void
}

export const useUpdateMaintenanceSolicitation = ({
  id,
  maintenanceSolicitation,
  onSuccess,
  onError,
}: UseUpdateMaintenanceSolicitationProps) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['updateMaintenanceSolicitation'],
    mutationFn: async (form: MaintenanceSolicitationValidationSchema) => {
      const solicitation = {
        photos_attachments_attributes: getRailsUpdate(
          maintenanceSolicitation?.photos || [],
          form.photos_attachments || [],
          (photo) => photo.id,
          (photo) => photo.id,
          (photo) => ({ id: photo.id, _destroy: true }),
          (photo) => ({ blob_id: photo.id })
        ),
        objectives_attributes: reconcileRailsUpdate(
          getRailsUpdate(
            maintenanceSolicitation?.objectives || [],
            form.objectives || [],
            (x) => x.id || '',
            (x) => x.id || '',
            mapToDeletion,
            (newObjective) => ({
              system_id: newObjective.system_id.value,
              part_id: newObjective.part_id.value,
              action_id: newObjective.action_id.value,
            }),
          ),
          (destroyed) => [destroyed.system.id, destroyed.part.id, destroyed.action.id],
          (created) => [created.system_id, created.part_id, created.action_id],
        ),
      }

      return privateAxios.put(`/maintenance/solicitations/${id}`, solicitation)
    },
    onSuccess,
    onError,
  })
}
